import React, { useState, useMemo } from 'react'

import {
    Dialog,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Typography,
    CircularProgress,
    makeStyles,
    TextField,
    InputAdornment,
    IconButton,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import CloseIcon from '@material-ui/icons/Close'
import BusinessIcon from '@material-ui/icons/Business'
import LocationOnIcon from '@material-ui/icons/LocationOn'

import { Company } from '../../../models'
import { CompanyWithInvoiceCode } from '../Property/types'

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            borderRadius: 12,
            maxHeight: '90vh',
        },
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
    },
    closeButton: {
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    dialogContent: {
        padding: theme.spacing(3),
        minWidth: 900,
    },
    searchContainer: {
        marginBottom: theme.spacing(3),
        position: 'relative',
    },
    searchField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: theme.palette.background.paper,
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
            },
        },
    },
    resultsInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    resultsCount: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    tableContainer: {
        borderRadius: 8,
        border: `1px solid ${theme.palette.divider}`,
        maxHeight: 'calc(100vh - 400px)',
        minHeight: 'calc(100vh - 400px)',
    },
    table: {
        minWidth: 900,
    },
    tableHead: {
        backgroundColor: theme.palette.grey[50],
        '& .MuiTableCell-head': {
            fontWeight: 600,
            color: theme.palette.text.primary,
            borderBottom: `2px solid ${theme.palette.divider}`,
            padding: theme.spacing(2),
        },
    },
    tableRow: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '&:nth-of-type(even)': {
            backgroundColor: theme.palette.background.default,
        },
    },
    tableCell: {
        padding: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    apartmentName: {
        fontWeight: 500,
        color: theme.palette.text.primary,
    },
    organizationName: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
    addressContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
    },
    addressIcon: {
        color: theme.palette.text.secondary,
        fontSize: '1rem',
        marginTop: 2,
    },
    addressText: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        lineHeight: 1.4,
    },
    modalLoading: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(6),
        minHeight: 'calc(100vh - 400px)',
        maxHeight: 'calc(100vh - 400px)',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(6),
        textAlign: 'center',
        minHeight: 200,
    },
    emptyStateIcon: {
        fontSize: 48,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
    },
    emptyStateText: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    emptyStateSubtext: {
        color: theme.palette.text.disabled,
        fontSize: '0.875rem',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: theme.spacing(2, 0, 0, 0),
        marginTop: theme.spacing(2),
    },
    pagination: {
        '& .MuiTablePagination-selectRoot': {
            marginRight: theme.spacing(1),
        },
    },
    closeActionButton: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 500,
        padding: theme.spacing(1, 3),
    },
}))

const TABLE_HEAD_CELLS = [
    { id: 'name', label: 'Apartment Name', width: 250 },
    { id: 'organization', label: 'Organization', width: 200 },
    { id: 'status', label: 'Relationship', width: 160 },
    { id: 'address', label: 'Address', width: 350 },
]

interface VendorApartmentModalProps {
    open: boolean
    onClose: () => void
    vendor: CompanyWithInvoiceCode | null
    apartments: Company[]
    isLoading: boolean
}

export const VendorApartmentModal: React.FC<VendorApartmentModalProps> = ({
    open,
    onClose,
    vendor,
    apartments,
    isLoading,
}) => {
    const classes = useStyles()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [searchText, setSearchText] = useState('')

    const filteredApartments = useMemo(() => {
        return apartments.filter((apartment) => {
            const searchLower = searchText.toLowerCase()
            return (
                apartment.name.toLowerCase().includes(searchLower) ||
                apartment.organization_name
                    ?.toLowerCase()
                    .includes(searchLower) ||
                (apartment.apartment_vendor_status || '')
                    .toLowerCase()
                    .includes(searchLower) ||
                (apartment.primary_address?.address1 || apartment.address || '')
                    .toLowerCase()
                    .includes(searchLower) ||
                (apartment.primary_address?.city || apartment.city || '')
                    .toLowerCase()
                    .includes(searchLower)
            )
        })
    }, [apartments, searchText])

    const paginatedApartments = useMemo(() => {
        const startIndex = page * rowsPerPage
        return filteredApartments.slice(startIndex, startIndex + rowsPerPage)
    }, [filteredApartments, page, rowsPerPage])

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleClose = () => {
        onClose()
        setSearchText('')
        setPage(0)
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            className={classes.dialog}
        >
            <div className={classes.dialogTitle}>
                <Typography variant="h6">
                    {vendor?.name} - Associated Apartments
                </Typography>
                <IconButton
                    onClick={handleClose}
                    className={classes.closeButton}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </div>

            <DialogContent className={classes.dialogContent}>
                <div className={classes.searchContainer}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search apartments by name, organization, or address..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className={classes.searchField}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>

                {isLoading ? (
                    <div className={classes.modalLoading}>
                        <CircularProgress size={40} />
                    </div>
                ) : filteredApartments.length > 0 ? (
                    <>
                        <TableContainer
                            component={Paper}
                            className={classes.tableContainer}
                            elevation={0}
                        >
                            <Table className={classes.table} size="medium">
                                <TableHead className={classes.tableHead}>
                                    <TableRow>
                                        {TABLE_HEAD_CELLS.map((cell) => (
                                            <TableCell
                                                key={cell.id}
                                                style={{ width: cell.width }}
                                                className={classes.tableCell}
                                            >
                                                {cell.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedApartments.map((apartment) => (
                                        <TableRow
                                            key={apartment.id}
                                            className={classes.tableRow}
                                        >
                                            <TableCell
                                                className={classes.tableCell}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    className={
                                                        classes.apartmentName
                                                    }
                                                >
                                                    {apartment.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableCell}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    className={
                                                        classes.organizationName
                                                    }
                                                >
                                                    {
                                                        apartment.organization_name
                                                    }
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableCell}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    className={
                                                        classes.organizationName
                                                    }
                                                >
                                                    {apartment.apartment_vendor_status ||
                                                        'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableCell}
                                            >
                                                <div
                                                    className={
                                                        classes.addressContainer
                                                    }
                                                >
                                                    <LocationOnIcon
                                                        className={
                                                            classes.addressIcon
                                                        }
                                                    />
                                                    <div>
                                                        <Typography
                                                            variant="body2"
                                                            className={
                                                                classes.addressText
                                                            }
                                                        >
                                                            {apartment.primary_address ? (
                                                                <>
                                                                    {
                                                                        apartment
                                                                            .primary_address
                                                                            .address1
                                                                    }
                                                                    <br />
                                                                    {
                                                                        apartment
                                                                            .primary_address
                                                                            .city
                                                                    }
                                                                    ,{' '}
                                                                    {
                                                                        apartment
                                                                            .primary_address
                                                                            .state
                                                                    }{' '}
                                                                    {
                                                                        apartment
                                                                            .primary_address
                                                                            .zip_code
                                                                    }
                                                                </>
                                                            ) : (
                                                                `${apartment.address}, ${apartment.city}, ${apartment.state} ${apartment.zip_code}`
                                                            )}
                                                        </Typography>
                                                    </div>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <div className={classes.footer}>
                            <TablePagination
                                count={filteredApartments.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onChangePage={handleChangePage}
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                className={classes.pagination}
                            />
                        </div>
                    </>
                ) : (
                    <div className={classes.emptyState}>
                        <BusinessIcon className={classes.emptyStateIcon} />
                        <Typography
                            variant="h6"
                            className={classes.emptyStateText}
                        >
                            {searchText
                                ? 'No apartments found'
                                : 'No apartments associated'}
                        </Typography>
                        <Typography
                            variant="body2"
                            className={classes.emptyStateSubtext}
                        >
                            {searchText
                                ? `No apartments match your search for "${searchText}"`
                                : 'This vendor is not associated with any apartments yet.'}
                        </Typography>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
